import React, { FC, Fragment, ReactNode, useMemo } from 'react';

import Link from '@o2/ui/Link';
import Typography from '@o2/ui/Typography';

interface FormattedHTMLProps {
  /**
   * The HTML to parse.
   */
  html?: string;

  /**
   * If true, unsupported HTML elements (such as `<marquee />` or `<section />`) will be treated as if they were mere spans.
   * Their children will be further parsed, but the tag itself will have no effect (and will be replaced with `<></>`).
   */
  parseUnknownAsString?: boolean;
}

const parser = new DOMParser();

const supportedAttributesA = ['href', 'target'];

const parseElementWithAttributes = (
  element: Element,
  children: React.ReactNode,
  parseUnknownAsString: boolean,
  indexInParent: number
): React.ReactNode => {
  const tagName = element.tagName.toLowerCase();

  switch (tagName) {
    case 'p':
    case 'span':
      return (
        <Typography component="span" key={indexInParent}>
          {children}
        </Typography>
      );
    case 'b':
    case 'strong':
      return (
        <Typography component="span" fontWeight="medium" key={indexInParent}>
          {children}
        </Typography>
      );
    case 'i':
      return (
        <Typography component="span" fontStyle="italic" key={indexInParent}>
          {children}
        </Typography>
      );
    case 'a': {
      const props = Array.from(element.attributes)
        .filter((attr) => supportedAttributesA.includes(attr.name))
        .reduce((props, attr) => {
          return {
            ...props,
            [attr.name]: attr.value,
          };
        }, {});

      return (
        <Link {...props} key={indexInParent}>
          {children}
        </Link>
      );
    }
    case 'br':
      return <br />;
    case 'h3':
      return (
        <Typography variant="h3" key={indexInParent}>
          {children}
        </Typography>
      );
    case 'h4':
      return (
        <Typography variant="h4" key={indexInParent}>
          {children}
        </Typography>
      );
    case 'body':
      // required for rendering the root node
      return <Fragment key={indexInParent}>{children}</Fragment>;
    default:
      console.warn(`Unsupported tagName: ${tagName}`);

      return parseUnknownAsString ? <Fragment key={indexInParent}>{children}</Fragment> : null;
  }
};

const parseDOMNode = (node: Node, parseUnknownAsString: boolean, indexInParent = 0): ReactNode => {
  const children = Array.from(node.childNodes).map((child, i) => parseDOMNode(child, parseUnknownAsString, i));

  if (node.nodeType === node.ELEMENT_NODE) {
    return parseElementWithAttributes(node as Element, children, parseUnknownAsString, indexInParent);
  } else if (node.nodeType === node.TEXT_NODE) {
    return <Fragment key={indexInParent}>{node.textContent}</Fragment>;
  } else {
    console.warn(`Unsupported nodeType: ${node.nodeType}`);

    if (!parseUnknownAsString) return null;
  }
};

/**
 * FormattedHTML is a tool for properly interpreting raw HTML from the CMS as the correctly styled design system components.
 * It should still be used very sparingly and only after consulting with O2 FE team!
 */
const FormattedHTML: FC<FormattedHTMLProps> = ({ html, parseUnknownAsString = false }) => {
  const restyled = useMemo(() => {
    if (!html) return null;

    const htmlDoc = parser.parseFromString(html, 'text/html');
    const body = htmlDoc.querySelector('body');

    if (!body) return null;

    return parseDOMNode(body, parseUnknownAsString);
  }, [html, parseUnknownAsString]);

  return <>{restyled}</>;
};

export default FormattedHTML;
